import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Billing = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "payment-method",
        "billing-period",
        "billing-history",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Billing Settings | xPage Help Center"
        description="Learn how to manage your billing settings in xPage, including payment methods, billing periods, subscription details, and access to your complete billing history."
        keywords="xPage billing, payment method, billing period, billing history, subscription payments, manage billing, invoices, receipts, store subscription"
        canonical="https://help.xpage.ai/help/settings/Billing"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Billing Settings
          </h1>
          <p className="text-sm text-systemLabel">
            The Billing Settings section in Xpage is where you manage all
            aspects of your store's financial transactions and subscription
            payments.
          </p>
          <p className="text-sm text-systemLabel">
            Maintaining accurate billing information ensures uninterrupted
            access to Xpage features, smooth subscription management, and
            transparent financial records.
          </p>
        </header>

        {/* Payment Method Section */}
        <section id="payment-method" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Payment Method
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Payment Management
                </h3>
                <p className="text-sm text-systemLabel">
                  Add, update, or remove your payment methods used for your
                  subscription.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Payment Security
                </h3>
                <p className="text-sm text-systemLabel">
                  Ensure your payment details are current to prevent service
                  interruptions.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Payment Options
                </h3>
                <p className="text-sm text-systemLabel">
                  Choose from supported payment options for secure, hassle-free
                  transactions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Billing Period Section */}
        <section id="billing-period" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Billing Period
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Billing Cycle Review
                </h3>
                <p className="text-sm text-systemLabel">
                  Review your billing cycle (monthly or annual) and next payment
                  date.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Subscription Timing
                </h3>
                <p className="text-sm text-systemLabel">
                  Understand the timing and frequency of subscription charges.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Budget Alignment
                </h3>
                <p className="text-sm text-systemLabel">
                  Align your billing period with your budgeting and business
                  planning needs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Billing History Section */}
        <section id="billing-history" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Billing History
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Transaction Records
                </h3>
                <p className="text-sm text-systemLabel">
                  Access a complete record of past invoices and transactions.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Document Downloads
                </h3>
                <p className="text-sm text-systemLabel">
                  Download receipts and invoices for accounting, bookkeeping, or
                  tax purposes.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Subscription Tracking
                </h3>
                <p className="text-sm text-systemLabel">
                  Track subscription changes, upgrades, or adjustments over
                  time.
                </p>
              </div>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                By keeping your billing information accurate and up-to-date, you
                ensure smooth store operations, reliable access to Xpage
                features, and full transparency over your subscription and
                payments.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#payment-method"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payment-method"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payment Method
              </a>
            </li>
            <li>
              <a
                href="#billing-period"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "billing-period"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Billing Period
              </a>
            </li>
            <li>
              <a
                href="#billing-history"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "billing-history"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Billing History
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Billing;
